import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useChannel } from '@ably-labs/react-hooks'
import type { Types } from 'ably'
import { useUserContext } from '~config/user-context'
import {
    constructChannel,
    ChannelTypeNames,
    ChatPresenceState,
    parseChatPresenceData,
    type ChatPresenceData,
} from '~utils/ably-helpers'

export interface ChatPresenceMember {
    /** Stable per-connection key for React rendering. */
    key: string
    userId: string
    firstName: string
    lastName?: string | null
    avatarUrl?: string | null
    state: ChatPresenceState
}

/** Idle time after the last keystroke before flipping back to viewing. */
const TYPING_IDLE_MS = 3000

// useChannel requires a message callback; presence-only usage ignores
// messages (ChatView already handles newMessage events on this channel).
function handleChannelMessage() {
    return undefined
}

/**
 * Tracks which other clinic users have a conversation open and whether
 * they're actively typing, via Ably presence on the conversation's existing
 * `newMessage-{chatId}` channel. No DB reads or writes: Ably removes members
 * automatically on disconnect/tab close, so indicators can't go stale.
 *
 * Call `reportTyping()` on every composer change; the hook debounces back to
 * viewing after `TYPING_IDLE_MS` of silence. Call `reportStoppedTyping()`
 * when the draft is sent or cleared.
 */
export function useChatPresence(chatId: string): {
    typingMembers: ChatPresenceMember[]
    viewingMembers: ChatPresenceMember[]
    reportTyping: () => void
    reportStoppedTyping: () => void
} {
    const { user } = useUserContext()

    // TEMP-DEBUG: ?debugPresence=1 shows your own presence so one person can
    // verify the pipeline solo. Remove after verification.
    const showSelf = useMemo(() => {
        if (typeof window === 'undefined') return false
        return new URLSearchParams(window.location.search).has('debugPresence')
    }, [])
    const channelName = useMemo(
        () => constructChannel(ChannelTypeNames.newMessage, chatId),
        [chatId],
    )
    const [channel] = useChannel(channelName, handleChannelMessage)
    const [presence, setPresence] = useState<Types.PresenceMessage[]>([])

    const selfPayload = useMemo<ChatPresenceData>(
        () => ({
            userId: user?.id ?? '',
            firstName: user?.first_name ?? '',
            lastName: user?.last_name ?? null,
            avatarUrl: user?.avatar_url ?? null,
            state: ChatPresenceState.Viewing,
        }),
        [user?.id, user?.first_name, user?.last_name, user?.avatar_url],
    )

    useEffect(() => {
        const presenceApi = channel.presence
        let cancelled = false

        const refresh = () => {
            presenceApi.get((err, members) => {
                if (err) {
                    console.error('[chat-presence] snapshot failed', err)
                    return
                }
                // TEMP-DEBUG: remove once presence is verified in dev.
                console.log(
                    '[chat-presence] snapshot',
                    channelName,
                    members?.map(member => member.data),
                )
                if (!cancelled) setPresence(members ?? [])
            })
        }
        const handlePresenceEvent = () => refresh()
        presenceApi.subscribe('enter', handlePresenceEvent)
        presenceApi.subscribe('leave', handlePresenceEvent)
        presenceApi.subscribe('update', handlePresenceEvent)

        // TEMP-DEBUG: remove once presence is verified in dev.
        console.log('[chat-presence] entering', channelName, selfPayload)
        presenceApi.enter(selfPayload, err => {
            if (err) {
                console.error('[chat-presence] enter failed', err)
            } else {
                // TEMP-DEBUG: remove once presence is verified in dev.
                console.log('[chat-presence] entered', channelName)
            }
            refresh()
        })

        return () => {
            cancelled = true
            presenceApi.unsubscribe('enter', handlePresenceEvent)
            presenceApi.unsubscribe('leave', handlePresenceEvent)
            presenceApi.unsubscribe('update', handlePresenceEvent)
            presenceApi.leave((leaveErr: Types.ErrorInfo | null) => {
                if (leaveErr) {
                    console.error('[chat-presence] leave failed', leaveErr)
                }
            })
        }
    }, [channel, channelName, selfPayload])

    const pushState = useCallback(
        (state: ChatPresenceState) => {
            // TEMP-DEBUG: remove once presence is verified in dev.
            console.log('[chat-presence] update', channelName, state)
            channel.presence.update({ ...selfPayload, state }, err => {
                if (err) {
                    console.error('[chat-presence] update failed', err)
                }
            })
        },
        [channel, channelName, selfPayload],
    )

    const currentStateRef = useRef<ChatPresenceState>(
        ChatPresenceState.Viewing,
    )
    const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Clear the pending idle flip on unmount so we never push a presence
    // update after Ably has removed us.
    useEffect(
        () => () => {
            if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current)
            }
        },
        [],
    )

    const reportStoppedTyping = useCallback(() => {
        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current)
            idleTimeoutRef.current = null
        }
        if (currentStateRef.current === ChatPresenceState.Typing) {
            currentStateRef.current = ChatPresenceState.Viewing
            pushState(ChatPresenceState.Viewing)
        }
    }, [pushState])

    const reportTyping = useCallback(() => {
        if (currentStateRef.current !== ChatPresenceState.Typing) {
            currentStateRef.current = ChatPresenceState.Typing
            pushState(ChatPresenceState.Typing)
        }
        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current)
        }
        idleTimeoutRef.current = setTimeout(
            reportStoppedTyping,
            TYPING_IDLE_MS,
        )
    }, [pushState, reportStoppedTyping])

    const { typingMembers, viewingMembers } = useMemo(() => {
        const typing: ChatPresenceMember[] = []
        const viewing: ChatPresenceMember[] = []

        for (const member of presence) {
            // Never show ourselves (covers all our tabs: same userId),
            // unless the solo-debug flag is on.
            const parsed = parseChatPresenceData(member.data)
            if (!parsed || (!showSelf && parsed.userId === user?.id)) continue

            const entry: ChatPresenceMember = {
                key:
                    member.connectionId ??
                    `${member.clientId}:${member.timestamp}`,
                ...parsed,
            }
            if (parsed.state === ChatPresenceState.Typing) {
                typing.push(entry)
            } else {
                viewing.push(entry)
            }
        }

        const byName = (a: ChatPresenceMember, b: ChatPresenceMember) =>
            a.firstName.localeCompare(b.firstName)
        typing.sort(byName)
        viewing.sort(byName)
        return { typingMembers: typing, viewingMembers: viewing }
    }, [presence, showSelf, user?.id])

    return { typingMembers, viewingMembers, reportTyping, reportStoppedTyping }
}
